import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { toast } from 'sonner';
import {
  ArrowLeft, ExternalLink, Copy, Settings, BarChart3, FolderOpen,
  FileCode, Clock, Globe, Rocket, Loader2, HardDrive, History,
  Terminal, ChevronRight, FilePlus, Upload, Package, Trash2, Save,
  X, Plus, ChevronDown, Image, File, FileText, GripVertical, Search, Eye
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import { motion } from 'framer-motion';

interface ProjectFile { name: string; size: number; type: string; uploaded: string; }
interface DeployLog { time: string; message: string; type: 'info' | 'success' | 'error' | 'highlight'; }
interface FileNode { name: string; isFolder: boolean; children?: FileNode[]; fullPath: string; }

export default function ProjectView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<any>(null);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployLogs, setDeployLogs] = useState<DeployLog[]>([]);
  const [showConsole, setShowConsole] = useState(false);
  const [consoleWidth, setConsoleWidth] = useState(340);
  const [deployDone, setDeployDone] = useState(false);
  const [deployedUrl, setDeployedUrl] = useState('');
  const [deployments, setDeployments] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'editor' | 'deployments'>('overview');
  const [showNewFile, setShowNewFile] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [renamingFile, setRenamingFile] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => { if (id) loadAll(); }, [id]);
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h); return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if ((e.ctrlKey||e.metaKey) && e.key==='s') { e.preventDefault(); if (dirty) saveFile(); } };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [dirty, selectedFile, fileContent]);
  useEffect(() => {
    const mm = (e: MouseEvent) => { if (!resizeRef.current) return; const d = resizeRef.current.startX - e.clientX; setConsoleWidth(Math.min(700, Math.max(260, resizeRef.current.startWidth + d))); };
    const mu = () => { resizeRef.current = null; };
    window.addEventListener('mousemove', mm); window.addEventListener('mouseup', mu);
    return () => { window.removeEventListener('mousemove', mm); window.removeEventListener('mouseup', mu); };
  }, []);

  async function loadAll() {
    try {
      const [proj, fileData, deployData] = await Promise.all([api.getProject(id!), api.getFiles(id!), api.getDeployments(id!)]);
      setProject(proj.project); setFiles(fileData.files||[]); setDeployments(deployData.deployments||[]);
    } catch (e: any) { toast.error(e.message); } finally { setLoading(false); }
  }

  async function openFile(name: string) {
    if (dirty && !confirm('Unsaved changes. Discard?')) return;
    try { const c = await api.getFileContent(id!, name); setSelectedFile(name); setFileContent(c); setOriginalContent(c); setDirty(false); setActiveTab('editor'); }
    catch (e: any) { toast.error(e.message); }
  }

  async function saveFile() {
    if (!selectedFile) return; setSaving(true);
    try { await api.updateFile(id!, selectedFile, fileContent); setOriginalContent(fileContent); setDirty(false); toast.success('Saved'); }
    catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  }

  async function createNewFile() {
    if (!newFileName.trim()) return;
    try { await api.updateFile(id!, newFileName.trim(), ''); toast.success('Created'); setFiles(p => p.find(f=>f.name===newFileName)?p:[...p,{name:newFileName.trim(),size:0,type:'text/plain',uploaded:new Date().toISOString()}]); setSelectedFile(newFileName.trim()); setFileContent(''); setOriginalContent(''); setDirty(false); setShowNewFile(false); setNewFileName(''); }
    catch (e: any) { toast.error(e.message); }
  }

  async function handleDeploy() {
    if (files.length===0) { toast.error('No files'); return; }
    if (dirty && selectedFile) await saveFile();
    setDeploying(true); setShowConsole(true); setDeployLogs([]); setDeployDone(false);
    const add = (m:string, t:DeployLog['type']='info') => { setDeployLogs(p=>[...p,{time:new Date().toLocaleTimeString('en-US',{hour12:false}),message:m,type:t}]); };
    add('▲ 𝐀𝐏𝐄𝐗𝐃𝐄𝐕 𝐃𝐄𝐏𝐋𝐎𝐘', 'highlight'); await sleep(400);
    add(`▪ ${files.length} file(s) to deploy`, 'info'); await sleep(300);
    add('▪ Copying to live environment...', 'info'); await sleep(800);
    try {
      const data = await api.deploy(id!);
      add('✔ Deployment successful', 'success'); await sleep(300);
      const url = data.deployment?.url || `https://${project.subdomain}.meganhost.dpdns.org`;
      setDeployedUrl(url); add(`✦ Live at: ${url}`, 'highlight');
      setDeployDone(true); setDeployments(p=>[data.deployment,...p]); setProject((p:any)=>({...p,status:'deployed'})); loadAll();
    } catch (e: any) { add(`✘ ${e.message}`, 'error'); } finally { setDeploying(false); }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const fl = e.target.files; if (!fl?.length) return;
    try { await api.uploadFiles(id!, Array.from(fl)); toast.success('Uploaded'); loadAll(); } catch (err:any) { toast.error(err.message); }
  }

  async function handleZipUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; if (!f) return;
    try { await api.uploadZip(id!, f); toast.success('Extracted'); loadAll(); } catch (err:any) { toast.error(err.message); }
  }

  async function deleteFile(name: string) {
    if (!confirm(`Delete ${name}?`)) return;
    try { await api.deleteFile(id!, name); setFiles(f=>f.filter(x=>x.name!==name)); if (selectedFile===name) { setSelectedFile(null); setFileContent(''); setDirty(false); } }
    catch (e:any) { toast.error(e.message); }
  }

  async function renameFile(oldName: string) {
    if (!renameValue.trim() || renameValue===oldName) { setRenamingFile(null); return; }
    try { const c = await api.getFileContent(id!, oldName); await api.updateFile(id!, renameValue, c); await api.deleteFile(id!, oldName); toast.success('Renamed'); setFiles(p=>p.map(f=>f.name===oldName?{...f,name:renameValue}:f)); if (selectedFile===oldName) setSelectedFile(renameValue); }
    catch (e:any) { toast.error(e.message); } setRenamingFile(null);
  }

  function getFileIcon(name: string) {
    const ext = name.split('.').pop()?.toLowerCase();
    if (['png','jpg','jpeg','gif','svg','webp','ico'].includes(ext||'')) return <Image size={14} className="text-accent-blue shrink-0" />;
    if (['html','htm'].includes(ext||'')) return <FileCode size={14} className="text-warning shrink-0" />;
    if (['css','scss','less'].includes(ext||'')) return <FileCode size={14} className="text-accent-blue shrink-0" />;
    if (['js','ts','jsx','tsx','mjs'].includes(ext||'')) return <FileCode size={14} className="text-warning shrink-0" />;
    return <File size={14} className="text-text-muted shrink-0" />;
  }

  function getLanguage(name: string): string {
    const ext = name.split('.').pop()?.toLowerCase();
    const m: Record<string,string> = { html:'html', htm:'html', css:'css', js:'javascript', jsx:'javascript', ts:'typescript', tsx:'typescript', json:'json' };
    return m[ext||''] || 'plaintext';
  }

  function formatBytes(b: number): string { if (!b) return '0 B'; if (b<1024) return b+' B'; if (b<1048576) return (b/1024).toFixed(1)+' KB'; return (b/1048576).toFixed(1)+' MB'; }
  function timeAgo(ts: number): string { if (!ts) return 'Never'; const d=Date.now()-ts; const m=Math.floor(d/60000); if (m<1) return 'Just now'; if (m<60) return m+'m ago'; const h=Math.floor(m/60); if (h<24) return h+'h ago'; return Math.floor(h/24)+'d ago'; }

  const fileTree = useMemo(() => buildTree(files), [files]);
  const filteredTree = useMemo(() => searchQuery ? filterTree(fileTree, searchQuery) : fileTree, [fileTree, searchQuery]);

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-accent" size={24} /></div>;
  if (!project) return <div className="p-6 text-center"><h2>Not found</h2><Link to="/dashboard" className="text-accent">Back</Link></div>;

  const projectUrl = `https://${project.subdomain}.meganhost.dpdns.org`;

  return (
    <div className="h-[calc(100vh-0px)] flex flex-col">
      {/* Top bar */}
      <div className="glass border-b border-border px-3 h-11 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Link to="/dashboard" className="text-text-muted hover:text-text-primary shrink-0"><ArrowLeft size={16} /></Link>
          <div className="w-6 h-6 rounded bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
            <FolderOpen size={12} className="text-accent" />
          </div>
          <span className="font-semibold text-xs truncate">{project.name}</span>
          <span className={`badge text-[10px] ${project.status==='deployed'?'badge-success':'badge-muted'}`}>{project.status}</span>
          {dirty && <span className="w-2 h-2 rounded-full bg-warning shrink-0" />}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {dirty && <button onClick={saveFile} disabled={saving} className="btn-primary text-[11px] py-1 px-3"><Save size={12} className="inline mr-1" />Save</button>}
          <button onClick={handleDeploy} disabled={deploying} className="btn-primary text-[11px] py-1 px-3"><Rocket size={12} className="inline mr-1" />Deploy</button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Files */}
        <div className="w-48 shrink-0 border-r border-border bg-surface/30 overflow-y-auto flex flex-col">
          <div className="p-2 border-b border-border space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-text-muted uppercase">Explorer</span>
              <div className="flex items-center gap-0.5">
                <button onClick={()=>setShowNewFile(true)} className="p-1 rounded hover:bg-elevated text-text-muted" title="New"><FilePlus size={13} /></button>
                <label className="cursor-pointer p-1 rounded hover:bg-elevated text-text-muted"><Upload size={13} /><input type="file" multiple onChange={handleFileUpload} className="hidden" /></label>
                <label className="cursor-pointer p-1 rounded hover:bg-elevated text-text-muted"><Package size={13} /><input type="file" accept=".zip" onChange={handleZipUpload} className="hidden" /></label>
              </div>
            </div>
            <div className="relative"><Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" /><input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search..." className="w-full bg-elevated border border-border rounded-md text-[11px] py-1 pl-7 pr-2 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent/50" /></div>
          </div>
          {showNewFile && (
            <div className="p-2 border-b border-border bg-elevated">
              <div className="flex gap-1"><input autoFocus value={newFileName} onChange={e=>setNewFileName(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')createNewFile();if(e.key==='Escape')setShowNewFile(false)}} placeholder="file.js or folder/file.js" className="input text-xs py-1 px-2 flex-1" /><button onClick={createNewFile} className="btn-primary text-[10px] px-2 py-1">OK</button><button onClick={()=>setShowNewFile(false)} className="p-1 text-text-muted"><X size={14} /></button></div>
            </div>
          )}
          <div className="flex-1 py-1">{renderTree(filteredTree, 0, expandedFolders, (p)=>setExpandedFolders(prev=>{const n=new Set(prev);n.has(p)?n.delete(p):n.add(p);return n}), openFile, selectedFile, deleteFile, getFileIcon, renamingFile, renameValue, setRenameValue, renameFile, setRenamingFile)}</div>
        </div>

        {/* Main */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-border flex items-center px-1 shrink-0">
            {(['overview','editor','deployments'] as const).map(tab => (
              <button key={tab} onClick={()=>setActiveTab(tab)} className={`px-3 py-1.5 text-[11px] font-medium capitalize border-b-2 -mb-[1px] transition-colors ${activeTab===tab?'border-accent text-accent':'border-transparent text-text-muted hover:text-text-primary'}`}>{tab}</button>
            ))}
          </div>

          <div className="flex-1 flex overflow-hidden">
            <div className={`${showConsole?'flex-1':'flex-1'} flex flex-col overflow-hidden`}>
              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                  <div className="max-w-2xl mx-auto space-y-5">
                    {/* URL Card */}
                    <div className="card">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-sm flex items-center gap-2"><Globe size={14} className="text-accent" /> Live URL</h3>
                        <span className={`badge text-[10px] ${project.status==='deployed'?'badge-success':'badge-muted'}`}>{project.status}</span>
                      </div>
                      <div className="flex items-center gap-2 bg-elevated rounded-lg px-3 py-2">
                        <code className="text-accent text-xs font-mono flex-1 truncate">{projectUrl}</code>
                        <button onClick={()=>{navigator.clipboard.writeText(projectUrl);toast.success('Copied!')}} className="p-1 rounded hover:bg-surface text-text-muted"><Copy size={14} /></button>
                        <a href={projectUrl} target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-surface text-text-muted"><ExternalLink size={14} /></a>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="card py-3 px-3"><FileCode size={14} className="text-accent mb-1" /><p className="text-lg font-bold">{project.file_count||0}</p><p className="text-[10px] text-text-muted">Files</p></div>
                      <div className="card py-3 px-3"><HardDrive size={14} className="text-accent-blue mb-1" /><p className="text-lg font-bold">{formatBytes(project.storage_bytes||0)}</p><p className="text-[10px] text-text-muted">Storage</p></div>
                      <div className="card py-3 px-3"><History size={14} className="text-accent-purple mb-1" /><p className="text-lg font-bold">{deployments.length}</p><p className="text-[10px] text-text-muted">Deploys</p></div>
                      <div className="card py-3 px-3"><Clock size={14} className="text-warning mb-1" /><p className="text-lg font-bold">{timeAgo(project.deployed_at)}</p><p className="text-[10px] text-text-muted">Last Deploy</p></div>
                    </div>

                    {/* Deploy History */}
                    <div className="card">
                      <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><History size={14} className="text-text-muted" /> Recent Deployments</h3>
                      <div className="space-y-1">
                        {deployments.slice(0,8).map(d => (
                          <div key={d.id} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-xs">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-mono text-[11px] text-text-muted shrink-0">#{d.id.slice(0,7)}</span>
                              <span className="text-text-muted">{d.file_count} files</span>
                            </div>
                            <span className="text-text-muted shrink-0">{timeAgo(d.created_at)}</span>
                          </div>
                        ))}
                        {deployments.length===0 && <p className="text-text-muted text-xs py-2">No deployments yet</p>}
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="card">
                      <h3 className="font-semibold text-sm mb-3">Quick Actions</h3>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={()=>{setActiveTab('editor');setShowNewFile(true)}} className="btn-secondary text-xs"><Plus size={12} className="inline mr-1" />New File</button>
                        <label className="btn-secondary text-xs cursor-pointer"><Upload size={12} className="inline mr-1" />Upload Files<input type="file" multiple onChange={handleFileUpload} className="hidden" /></label>
                        <label className="btn-secondary text-xs cursor-pointer"><Package size={12} className="inline mr-1" />Upload Zip<input type="file" accept=".zip" onChange={handleZipUpload} className="hidden" /></label>
                        <Link to={`/project/${id}/editor`} className="btn-primary text-xs"><FileCode size={12} className="inline mr-1" />Open Full Editor</Link>
                        <Link to={`/project/${id}/settings`} className="btn-secondary text-xs"><Settings size={12} className="inline mr-1" />Settings</Link>
                        <Link to={`/project/${id}/analytics`} className="btn-secondary text-xs"><BarChart3 size={12} className="inline mr-1" />Analytics</Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* EDITOR TAB */}
              {activeTab === 'editor' && (
                selectedFile ? (
                  <div className="flex-1 flex flex-col">
                    <div className="border-b border-border px-3 py-1 flex items-center justify-between shrink-0 bg-surface/50">
                      <div className="flex items-center gap-2 text-[11px]">
                        {getFileIcon(selectedFile)}
                        {renamingFile===selectedFile ? (
                          <input autoFocus value={renameValue} onChange={e=>setRenameValue(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')renameFile(selectedFile);if(e.key==='Escape')setRenamingFile(null)}} onBlur={()=>renameFile(selectedFile)} className="input text-[11px] py-0.5 px-1 w-40" />
                        ) : (
                          <span onDoubleClick={()=>{setRenamingFile(selectedFile);setRenameValue(selectedFile)}}>{selectedFile}</span>
                        )}
                        {dirty && <span className="text-warning text-[10px]">• Unsaved</span>}
                      </div>
                      <button onClick={saveFile} disabled={!dirty||saving} className="text-[10px] text-accent hover:underline">{saving?'Saving...':'Save'}</button>
                    </div>
                    <div className="flex-1">
                      <Editor height="100%" language={getLanguage(selectedFile)} value={fileContent}
                        onChange={v=>{setFileContent(v||'');setDirty((v||'')!==originalContent)}} theme="vs-dark"
                        options={{ minimap:{enabled:false}, fontSize:13, fontFamily:'Geist Mono, JetBrains Mono, monospace', padding:{top:12}, scrollBeyondLastLine:false, automaticLayout:true, wordWrap:'on', formatOnPaste:true, tabSize:2, smoothScrolling:true, cursorBlinking:'smooth' }} />
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center"><div className="text-center"><FileCode size={32} className="mx-auto mb-2 text-text-muted" /><p className="text-text-muted text-sm">Select a file to edit</p><button onClick={()=>setShowNewFile(true)} className="btn-secondary text-xs mt-3"><Plus size={14} /> New File</button></div></div>
                )
              )}

              {/* DEPLOYMENTS TAB */}
              {activeTab === 'deployments' && (
                <div className="flex-1 overflow-y-auto p-4">
                  <h3 className="text-sm font-semibold mb-4">Deployment History</h3>
                  <div className="space-y-1.5">
                    {deployments.map(d => (
                      <div key={d.id} className="card py-2.5 px-3 flex items-center justify-between">
                        <div><p className="text-xs font-medium font-mono">#{d.id.slice(0,8)}</p><p className="text-[10px] text-text-muted">{d.file_count} files · {formatBytes(d.storage_bytes)} · {new Date(d.created_at).toLocaleString()}</p></div>
                        <span className="badge badge-success text-[10px]">{d.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Console */}
            {showConsole && (
              <>
                <div className="w-1 cursor-col-resize hover:bg-accent/30 shrink-0 flex items-center justify-center" onMouseDown={e=>{resizeRef.current={startX:e.clientX,startWidth:consoleWidth}}}><GripVertical size={12} className="text-text-muted opacity-0 hover:opacity-100" /></div>
                <div style={{width:consoleWidth}} className="shrink-0 border-l border-border bg-[#0A0A0A] flex flex-col">
                  <div className="border-b border-border px-3 py-2 flex items-center justify-between shrink-0"><div className="flex items-center gap-2"><Terminal size={13} className="text-accent" /><span className="text-[11px] font-semibold">Console</span></div><button onClick={()=>{setShowConsole(false);setDeployDone(false)}} className="p-1 rounded hover:bg-elevated"><X size={13} /></button></div>
                  <div className="flex-1 overflow-y-auto p-3 font-mono text-[11px] space-y-0.5">
                    {deployLogs.map((l,i)=><div key={i} className={`flex gap-2 ${l.type==='error'?'text-danger':l.type==='success'?'text-accent':l.type==='highlight'?'text-accent-blue font-bold':'text-text-muted'}`}><span className="shrink-0">{l.time}</span><span>{l.message}</span></div>)}
                    {deploying && <div className="flex gap-2 text-text-muted"><Loader2 size={12} className="animate-spin" /></div>}
                  </div>
                  {deployDone && deployedUrl && (
                    <div className="p-3 border-t border-border space-y-2">
                      <button onClick={()=>window.open(deployedUrl,'_blank')} className="btn-primary w-full text-xs py-2"><ExternalLink size={12} className="inline mr-1" />Open Site</button>
                      <button onClick={()=>{navigator.clipboard.writeText(deployedUrl);toast.success('Copied!')}} className="btn-secondary w-full text-xs py-2"><Copy size={12} className="inline mr-1" />Copy URL</button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div className="glass border-t border-border h-6 flex items-center px-3 justify-between text-[10px] text-text-muted shrink-0">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className={`w-1.5 h-1.5 rounded-full ${project.status==='deployed'?'bg-accent':'bg-warning'}`} />{project.status}</span>
          <span>{files.length} files</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to={`/project/${id}/settings`} className="hover:text-text-primary">Settings</Link>
          <Link to={`/project/${id}/analytics`} className="hover:text-text-primary">Analytics</Link>
          <span>ApexDev</span>
        </div>
      </div>
    </div>
  );
}

// File tree helpers
function buildTree(files: ProjectFile[]): FileNode[] {
  const root: FileNode[] = []; const fm = new Map<string, FileNode>();
  for (const f of files) {
    const parts = f.name.split('/'); let cur = root; let cp = '';
    for (let i=0;i<parts.length;i++) {
      const p = parts[i]; cp = cp?`${cp}/${p}`:p; const last = i===parts.length-1;
      if (last) cur.push({name:p,isFolder:false,fullPath:f.name});
      else { let folder= fm.get(cp); if(!folder) { folder={name:p,isFolder:true,children:[],fullPath:cp}; fm.set(cp,folder); cur.push(folder); } cur = folder.children!; }
    }
  }
  const srt=(n:FileNode[])=>{n.sort((a,b)=>a.isFolder!==b.isFolder?(a.isFolder?-1:1):a.name.localeCompare(b.name));for(const x of n) if(x.children) srt(x.children);};
  srt(root); return root;
}

function filterTree(nodes: FileNode[], q: string): FileNode[] {
  const r: FileNode[]=[]; const ql=q.toLowerCase();
  for (const n of nodes) {
    if (n.name.toLowerCase().includes(ql)) r.push(n);
    else if (n.children) { const f = filterTree(n.children,q); if (f.length) r.push({...n,children:f}); }
  }
  return r;
}

function renderTree(nodes: FileNode[], depth: number, expanded: Set<string>, toggle: (p:string)=>void, onSelect:(n:string)=>void, selected:string|null, onDelete:(n:string)=>void, getIcon:(n:string)=>JSX.Element, renaming:string|null, rv:string, srv:(v:string)=>void, rn:(o:string)=>void, srn:(v:string|null)=>void): JSX.Element[] {
  const els: JSX.Element[]=[];
  for (const n of nodes) {
    if (n.isFolder) {
      const ex = expanded.has(n.fullPath);
      els.push(<div key={n.fullPath} className="px-2 py-0.5 text-[11px] text-text-muted flex items-center gap-1.5 cursor-pointer hover:bg-elevated" style={{paddingLeft:8+depth*12}} onClick={()=>toggle(n.fullPath)}>{ex?<ChevronDown size={10}/>:<ChevronRight size={10}/>}<File size={12} className="text-accent-blue shrink-0"/><span className="truncate">{n.name}</span></div>);
      if (ex && n.children) els.push(...renderTree(n.children,depth+1,expanded,toggle,onSelect,selected,onDelete,getIcon,renaming,rv,srv,rn,srn));
    } else {
      els.push(<div key={n.fullPath} onClick={()=>onSelect(n.fullPath)} onContextMenu={e=>{e.preventDefault();srn(n.fullPath);srv(n.fullPath)}} className={`w-full text-left px-2 py-1.5 text-[11px] flex items-center gap-1.5 hover:bg-elevated cursor-pointer group ${selected===n.fullPath?'bg-elevated text-accent border-l-2 border-l-accent':'text-text-primary border-l-2 border-l-transparent'}`} style={{paddingLeft:8+depth*12}}>{getIcon(n.name)}{renaming===n.fullPath?<input autoFocus value={rv} onChange={e=>srv(e.target.value)} onKeyDown={e=>{e.stopPropagation();if(e.key==='Enter')rn(n.fullPath);if(e.key==='Escape')srn(null)}} onBlur={()=>rn(n.fullPath)} onClick={e=>e.stopPropagation()} className="input text-[11px] py-0.5 px-1 flex-1"/>:<span className="truncate flex-1">{n.name}</span>}<button onClick={e=>{e.stopPropagation();onDelete(n.fullPath)}} className="opacity-0 group-hover:opacity-100 hover:text-danger p-0.5"><Trash2 size={11}/></button></div>);
    }
  }
  return els;
}

function sleep(ms: number) { return new Promise(r=>setTimeout(r,ms)); }
